import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  ScrollView,
  Modal,
  ActivityIndicator,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import { openCheckoutSession } from '../../utils/checkout';
import { trackAuthEvent } from '../../services/analytics';
import { Text } from '@/components/ui/text';
import { colors } from '@/design-system/theme/colors';
import { creditsAPI } from '@/services/api';
import { CreditPack, CreditPackList } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';
import { useCreditUpdates } from '@/context/WebSocketContext';
import {
  createCheckoutError,
  showCheckoutFailureDialog,
} from '@/utils/checkoutFailures';

const CHECKOUT_SURFACE = 'credit_purchase_modal';

interface CreditPurchaseModalProps {
  visible: boolean;
  onClose: () => void;
  onPurchaseComplete?: () => void;
  schoolId?: number; // For B2B2C admin purchasing for school
}

/**
 * CreditPurchaseModal - Bottom sheet/modal for purchasing credit packs
 *
 * Features:
 * - Fetches available packs from API
 * - Displays packs with pricing in user's currency
 * - Opens Stripe checkout in WebBrowser
 * - Handles success/cancel returns
 * - Success notification is handled by CreditContext via toast
 */
export function CreditPurchaseModal({
  visible,
  onClose,
  onPurchaseComplete,
  schoolId,
}: CreditPurchaseModalProps) {
  const { t } = useTranslation();
  const { lastCreditUpdate } = useCreditUpdates();
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState<string | null>(null);
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null);
  const [comingSoon, setComingSoon] = useState(false);
  const pendingCheckoutRef = useRef<{ initialTotal: number } | null>(null);
  const pollingRef = useRef(false);

  // Track WebSocket credit updates that arrive while the modal is open and purchasing
  const purchaseStartTimestampRef = useRef<number>(0);
  // Ref to track if WebSocket already handled the purchase (for async closure in handlePurchase)
  const wsHandledRef = useRef(false);

  // Fetch available credit packs
  const fetchPacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    setComingSoon(false);
    try {
      const response: CreditPackList = await creditsAPI.getPacks();
      // Check if feature is disabled (coming soon)
      if (response.enabled === false || response.coming_soon === true) {
        setComingSoon(true);
        setPacks([]);
      } else {
        setPacks(response.packs || []);
      }
      setCurrency(response.currency || 'USD');
    } catch (err: any) {
      console.error('Failed to fetch credit packs:', err);
      setError(t('dashboard.failedToLoadPacks'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch packs when modal opens
  useEffect(() => {
    if (visible) {
      fetchPacks();
      setSelectedPackId(null);
      wsHandledRef.current = false;
    }
  }, [visible, fetchPacks]);

  // React to WebSocket credit updates while modal is open
  useEffect(() => {
    if (!visible || !lastCreditUpdate) return;
    // Only handle updates that arrived after we started purchasing
    if (lastCreditUpdate.timestamp <= purchaseStartTimestampRef.current) return;

    if (lastCreditUpdate.action === 'purchase_complete') {
      setPurchasing(false);
      setSelectedPackId(null);
      wsHandledRef.current = true;
      onPurchaseComplete?.();
      // Close modal — toast notification is handled by CreditContext
      onClose();
    } else if (lastCreditUpdate.action === 'purchase_failed') {
      setPurchasing(false);
      setSelectedPackId(null);
      void showCheckoutFailureDialog({
        error: createCheckoutError('payment_notification_failed'),
        surface: CHECKOUT_SURFACE,
        checkoutKey: selectedPackId ? String(selectedPackId) : 'unknown_pack',
        properties: {
          product_type: 'credit_pack',
          school_checkout: typeof schoolId === 'number',
        },
        t,
      });
    }
  }, [visible, lastCreditUpdate, onPurchaseComplete, onClose, schoolId, selectedPackId, t]);

  // Format price for display
  const formatPrice = (priceCents: number | null | undefined, currencyCode: string): string => {
    if (priceCents == null) return 'N/A';
    const code = currencyCode.toUpperCase();
    const locale = code === 'TRY' ? 'tr-TR' : code === 'EUR' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).format(priceCents / 100);
  };

  // Handle purchase
  const resolveRedirectUrl = useCallback(() => {
    if (Platform.OS === 'web') {
      const appBase = process.env.EXPO_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '');
      return `${appBase.replace(/\/$/, '')}/checkout-complete`;
    }
    return Linking.createURL('checkout-complete');
  }, []);

  const fetchTotalAvailable = useCallback(async () => {
    if (typeof schoolId === 'number') {
      const status = await creditsAPI.getSchoolStatus(schoolId);
      return typeof status.total_available === 'number' ? status.total_available : 0;
    }
    const status = await creditsAPI.getStatus();
    return typeof status.total_available === 'number' ? status.total_available : 0;
  }, [schoolId]);

  const pollForCreditUpdate = useCallback(async (initialTotal: number) => {
    const maxAttempts = 10;
    const delayMs = 2000;
    let lastTotal = initialTotal;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const currentTotal = await fetchTotalAvailable();
        lastTotal = currentTotal;
        if (currentTotal > initialTotal) {
          return { increased: true, delta: currentTotal - initialTotal };
        }
      } catch (err) {
        console.warn('Failed to refresh credits during polling:', err);
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return { increased: false, delta: Math.max(lastTotal - initialTotal, 0) };
  }, [fetchTotalAvailable]);

  const runPostCheckout = useCallback(async (initialTotal: number) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const pollResult = await pollForCreditUpdate(initialTotal);
      if (pollResult.increased && pollResult.delta > 0) {
        showDialog(
          t('dashboard.purchaseSuccess'),
          `+${pollResult.delta} ${t('dashboard.creditsAdded')}`,
          [{ text: t('dashboard.ok') }]
        );
      } else {
        showDialog(
          t('dashboard.purchaseProcessing'),
          t(
            'dashboard.purchaseProcessingMessage'
          ),
          [{ text: t('dashboard.ok') }]
        );
      }
      onPurchaseComplete?.();
    } finally {
      pollingRef.current = false;
    }
  }, [onPurchaseComplete, pollForCreditUpdate, t]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handler = (event: MessageEvent) => {
      if (!pendingCheckoutRef.current) return;
      if (typeof window !== 'undefined' && event.origin !== window.location.origin) return;
      const payload = event.data as { type?: string };
      if (payload?.type !== 'credit_checkout_complete') return;
      const initialTotal = pendingCheckoutRef.current.initialTotal;
      pendingCheckoutRef.current = null;
      runPostCheckout(initialTotal);
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [runPostCheckout]);

  const handlePurchase = async (pack: CreditPack) => {
    setPurchasing(true);
    setSelectedPackId(pack.id);
    wsHandledRef.current = false;
    purchaseStartTimestampRef.current = Date.now();
    const packProps = {
      surface: CHECKOUT_SURFACE,
      product_type: 'credit_pack',
      pack_id: pack.id,
      school_checkout: typeof schoolId === 'number',
    };
    trackAuthEvent('credit_pack_checkout_initiated', packProps);
    try {
      let initialTotal = 0;
      try {
        initialTotal = await fetchTotalAvailable();
      } catch (err) {
        console.warn('Failed to snapshot credits before checkout:', err);
      }

      const redirectUrl = resolveRedirectUrl();
      const response = await creditsAPI.checkout(pack.id, schoolId, redirectUrl);

      if (!response.checkout_url) {
        throw createCheckoutError('missing_checkout_url');
      }
      pendingCheckoutRef.current = { initialTotal };
      trackAuthEvent('checkout_session_created', packProps);
      const result = await openCheckoutSession(response.checkout_url, redirectUrl);

      // After returning from browser — if WebSocket already notified us, wsHandledRef
      // will be true and we don't need to do anything else.
      if (!wsHandledRef.current) {
        if (result.type === 'dismiss' || result.type === 'cancel') {
          // Fallback: call onPurchaseComplete so the parent can refresh status.
          // The WebSocket path (via CreditContext) will also refresh when the
          // notification eventually arrives.
          onPurchaseComplete?.();
        }
      }
    } catch (err: any) {
      console.error('Checkout failed:', err);
      pendingCheckoutRef.current = null;
      await showCheckoutFailureDialog({
        error: err,
        surface: CHECKOUT_SURFACE,
        checkoutKey: String(pack.id),
        properties: {
          product_type: 'credit_pack',
          pack_id: pack.id,
          school_checkout: typeof schoolId === 'number',
        },
        retry: () => handlePurchase(pack),
        t,
      });
    } finally {
      // Only reset purchasing state if WebSocket hasn't already handled it
      if (!wsHandledRef.current) {
        setPurchasing(false);
        setSelectedPackId(null);
      }
    }
  };

  // Render header
  const renderHeader = () => (
    <View className="flex-row items-center justify-between p-4 border-b border-gray-200">
      <View className="flex-1">
        <Text className="text-xl font-bold text-gray-900">
          {t('dashboard.buyCredits')}
        </Text>
        <Text className="text-sm text-gray-500 mt-0.5">
          {schoolId
            ? t('dashboard.addCreditsToSchool')
            : t('dashboard.getMoreAnalysisCredits')}
        </Text>
      </View>
      <TouchableOpacity
        onPress={onClose}
        className="w-10 h-10 items-center justify-center rounded-full bg-gray-100"
        disabled={purchasing}
      >
        <MaterialIcons name="close" size={24} color={colors.gray[700]} />
      </TouchableOpacity>
    </View>
  );

  // Render loading state
  const renderLoading = () => (
    <View className="flex-1 items-center justify-center py-12">
      <ActivityIndicator size="large" color={colors.primary} />
      <Text className="text-sm text-gray-500 mt-3">
        {t('dashboard.loadingCreditPacks')}
      </Text>
    </View>
  );

  // Render error state
  const renderError = () => (
    <View className="flex-1 items-center justify-center py-12 px-6">
      <View className="w-16 h-16 rounded-full bg-red-50 items-center justify-center mb-4">
        <MaterialIcons name="error-outline" size={32} color={colors.error} />
      </View>
      <Text className="text-base text-gray-700 text-center">{error}</Text>
      <TouchableOpacity
        onPress={fetchPacks}
        className="mt-4 px-6 py-2 rounded-lg bg-primary"
      >
        <Text className="text-white font-semibold">
          {t('dashboard.tryAgain')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  // Render coming soon state
  const renderComingSoon = () => (
    <View className="flex-1 items-center justify-center py-12 px-6">
      <View className="w-20 h-20 rounded-full bg-blue-50 items-center justify-center mb-4">
        <MaterialIcons name="schedule" size={40} color={colors.primary} />
      </View>
      <Text className="text-xl font-bold text-gray-900 text-center mb-2">
        {t('dashboard.comingSoon')}
      </Text>
      <Text className="text-base text-gray-500 text-center mb-6 px-4">
        {t('dashboard.comingSoonMessage')}
      </Text>
      <TouchableOpacity
        onPress={onClose}
        className="px-8 py-3 rounded-xl bg-primary"
      >
        <Text className="text-white font-semibold">
          {t('dashboard.gotIt')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  // Compute the id of the pack with the lowest price-per-credit so the
  // "Best Value" badge always points at the actual best deal.
  const bestValuePackId = useMemo(() => {
    let bestId: string | number | null = null;
    let bestRate = Infinity;
    for (const p of packs) {
      if (p.purchasable === false || !p.price_cents || !p.credits) continue;
      const rate = p.price_cents / p.credits;
      if (rate < bestRate) {
        bestRate = rate;
        bestId = p.id;
      }
    }
    return bestId;
  }, [packs]);

  // Render pack card
  const renderPackCard = (pack: CreditPack) => {
    const isSelected = selectedPackId === pack.id;
    const isPurchasing = purchasing && isSelected;
    const canPurchase = pack.purchasable !== false;
    const pricePerCredit = pack.price_cents ? pack.price_cents / pack.credits : null;
    const isBestValue = pack.id === bestValuePackId;

    return (
      <TouchableOpacity
        key={pack.id}
        onPress={() => canPurchase && handlePurchase(pack)}
        disabled={purchasing || !canPurchase}
        activeOpacity={canPurchase ? 0.7 : 1}
        className={`
          mb-3 rounded-xl border-2 overflow-hidden
          ${isPurchasing ? 'border-primary bg-blue-50' : !canPurchase ? 'border-gray-100 bg-gray-50' : 'border-gray-200 bg-white'}
        `}
      >
        <View className="p-4">
          <View className="flex-row items-center justify-between">
            {/* Credits info */}
            <View className="flex-row items-center gap-3">
              <View className={`w-12 h-12 rounded-full items-center justify-center ${canPurchase ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <MaterialIcons name="toll" size={24} color={canPurchase ? colors.primary : colors.gray[400]} />
              </View>
              <View>
                <Text className={`text-lg font-bold ${canPurchase ? 'text-gray-900' : 'text-gray-400'}`}>
                  {pack.credits} {pack.credits === 1
                    ? t('dashboard.credit')
                    : t('dashboard.creditPlural')}
                </Text>
                {canPurchase && pricePerCredit && pack.credits > 1 && (
                  <Text className="text-xs text-gray-500">
                    {formatPrice(Math.round(pricePerCredit), currency)} {t('dashboard.perCredit')}
                  </Text>
                )}
                {(pack.transcription_minutes_granted ?? 0) > 0 && (
                  <Text className={`text-xs ${canPurchase ? 'text-gray-500' : 'text-gray-400'}`}>
                    + {pack.transcription_minutes_granted} {t('dashboard.transcriptionMinutes')}
                  </Text>
                )}
                {!canPurchase && (
                  <Text className="text-xs text-gray-400">
                    {t('dashboard.packNotAvailableYet')}
                  </Text>
                )}
              </View>
            </View>

            {/* Price */}
            <View className="items-end">
              {isPurchasing ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : canPurchase ? (
                <>
                  <Text className="text-xl font-bold text-primary">
                    {formatPrice(pack.price_cents, currency)}
                  </Text>
                  {isBestValue && (
                    <View className="mt-1 px-2 py-0.5 rounded-full bg-green-100">
                      <Text className="text-xs font-semibold text-green-700">
                        {t('dashboard.bestValue')}
                      </Text>
                    </View>
                  )}
                </>
              ) : (
                <Text className="text-sm text-gray-400">
                  {t('dashboard.comingSoon')}
                </Text>
              )}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // Render packs list
  const renderPacks = () => {
    if (packs.length === 0) {
      return (
        <View className="flex-1 items-center justify-center py-12 px-6">
          <View className="w-16 h-16 rounded-full bg-gray-100 items-center justify-center mb-4">
            <MaterialIcons name="toll" size={32} color={colors.gray[400]} />
          </View>
          <Text className="text-base font-semibold text-gray-700 text-center mb-2">
            {t('dashboard.noPacksAvailable')}
          </Text>
          <Text className="text-sm text-gray-500 text-center">
            {t('dashboard.noPacksAvailableMessage')}
          </Text>
        </View>
      );
    }

    return (
      <ScrollView className="flex-1 px-4 py-4">
        {/* Info banner */}
        <View className="flex-row items-start gap-2 mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100">
          <MaterialIcons name="info-outline" size={18} color={colors.info} />
          <Text className="flex-1 text-xs text-blue-700 leading-4">
            {t('dashboard.creditsNeverExpireInfo')}
          </Text>
        </View>

        {/* Pack cards */}
        {packs.map(renderPackCard)}

        {/* Footer note */}
        <View className="items-center py-4">
          <View className="flex-row items-center gap-1">
            <MaterialIcons name="lock" size={14} color={colors.gray[400]} />
            <Text className="text-xs text-gray-400">
              {t('dashboard.secureCheckout')}
            </Text>
          </View>
        </View>
      </ScrollView>
    );
  };

  // Determine which content to render
  const renderContent = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (comingSoon) return renderComingSoon();
    return renderPacks();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : 'fullScreen'}
      onRequestClose={onClose}
    >
      <View className="flex-1 bg-gray-50">
        {renderHeader()}
        {renderContent()}
      </View>
    </Modal>
  );
}

export default CreditPurchaseModal;
